<!--
  The base badge component.
-->
<script setup lang="ts">import { computed as _computed } from 'vue';

import { useListeners } from '@shared/vue'
import { isFunction } from '@shared/functions'
import { faEllipsis, faX } from '@fortawesome/pro-light-svg-icons'

export interface BadgeProps {
  size?: 'sm' | 'md' | 'lg'

  // The label to display in the badge.
  label?: string | false | number

  // The type of badge to display.
  type?: 'default' | 'info' | 'success' | 'warning' | 'error' | 'chip'

  // Whether it should have less horizontal padding.
  compact?: boolean

  // To avoid displaying a close button.
  closable?: boolean

  // Whether the tag indicates there might be more tags.
  more?: boolean

  // Whether it should be round.
  rounded?: boolean

  // To avoid setting background styles. This is useful when you want custom colors and want to avoid CSS conflicts
  noBackground?: boolean

  // Styles to apply to the badge label.
  labelClass?: string
}

withDefaults(defineProps<BadgeProps>(), { size: 'sm',type: 'default',closable: false,noBackground: false, })

const emit = defineEmits(['close'])

const styles = {
  default: 'bg-dark-300 text-white',
  error: 'bg-red-400 text-white',
  info: 'bg-blue-400 text-white',
  warning: 'bg-gold-400 text-white',
  success: 'bg-green-400 text-white',
  chip: 'bg-surface-blue text-navy',
}

const hoverStyles = {
  default: 'hover:bg-dark-400 hover:text-white',
  error: 'hover:bg-red-500 hover:text-white',
  info: 'hover:bg-blue-500 hover:text-white',
  warning: 'hover:bg-gold-500 hover:text-white',
  success: 'hover:bg-green-500 hover:text-white',
  chip: 'hover:bg-primary-pale',
}

// Whether the tag can be clicked.
const clickable = useListeners(listeners => isFunction(listeners.onClick))

const textSizes = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
}
const textSize = _computed(() => textSizes[__props.size])

const color = _computed(() => {
  return __props.noBackground ? 'text-g' : styles[__props.type]
})

const hoverClasses = _computed(() => {
  if (!__props.closable && !clickable) return ''
  if (__props.noBackground) return 'cursor-pointer'

  return `${hoverStyles[__props.type]} cursor-pointer`
})
</script>

<template>
  <div
    v-if="label || more"
    class="badge inline-flex items-center justify-center whitespace-nowrap group"
    :class="[
      color,
      textSize,
      hoverClasses,
      clickable && 'cursor-pointer',
      noBackground && 'border-1 border-grey-primary',
      type,
      { compact, rounded },
    ]"
  >
    <slot name="pre"/>
    <div v-if="label" v-truncate :class="labelClass">{{ label }}</div>
    <GIcon v-if="more" size="10px" :icon="faEllipsis"/>
    <GIcon
      v-if="closable"
      :icon="faX"
      size="10px"
      class="close-button transition-all"
      @click.prevent="emit('close')"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "sass:math";

$ratio: 18;
$px: math.div(12em, $ratio);
$pl: math.div(10em, $ratio);

.badge {
  border-radius: math.div(5em, $ratio);
  height: math.div(24em, $ratio);
  padding: 0 $px;
  vertical-align: baseline;
  white-space: nowrap;

  &.compact {
    padding: 0 math.div(5em, $ratio);
  }

  &.rounded {
    border-radius: 50%;
    width: math.div(24em, $ratio);
  }

  &:hover {
    .close-button {
      color: black;
    }
  }
}

.chip {
  @apply px-2 py-1;
  @apply rounded-v2md;
  @apply gap-1;
  @apply transition-all;

  height: auto;
}

.close-button {
  --size: #{$pl};

  $py: math.div(7em, $ratio);
  $my: $py * -1;
  $mx: $px * -1;

  margin: $my $mx $my 0 !important;
  padding: $py $px;
  padding-left: $pl;
}
</style>
